import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Shield,
  Activity,
  ScanLine,
  FileCheck,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  Calendar,
  Search,
  Download } from
'lucide-react';
import { toast } from 'sonner';

const API_URL = API_BASE;

const TrailDashboard = () => {const { t } = useTranslation("wms");
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(24);

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/trails/stats?hours=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch trail stats:', error);
      // Use mock data
      setStats({
        period_hours: period,
        events: { AUDIT: 45, OPERATIONAL: 128, SCAN: 312, PROOF: 67 },
        scans: { OK: 298, REJECTED: 8, WARNING: 6 },
        pods: { DELIVERED: 52, FAILED: 8, PARTIAL: 7 },
        totals: { events: 552, scans: 312, pods: 67 }
      });
    }
    setLoading(false);
  };

  const trailTypes = [
  {
    id: 'audit',
    name: 'Audit Trail',
    description: 'Who, what, when, where - compliance records',
    icon: Shield,
    color: 'bg-blue-500',
    href: '/wms/trails/audit',
    count: stats?.events?.AUDIT || 0
  },
  {
    id: 'operational',
    name: 'Operational Trail',
    description: 'Status timeline - Uber-style tracking',
    icon: Activity,
    color: 'bg-green-500',
    href: '/wms/trails/operational',
    count: stats?.events?.OPERATIONAL || 0
  },
  {
    id: 'scan',
    name: 'Scan Trail',
    description: 'Barcode proof chain - Amazon-style',
    icon: ScanLine,
    color: 'bg-amber-500',
    href: '/wms/trails/scan',
    count: stats?.events?.SCAN || stats?.totals?.scans || 0
  },
  {
    id: 'proof',
    name: 'Proof Trail',
    description: 'POD signatures, photos, GPS stamps',
    icon: FileCheck,
    color: 'bg-purple-500',
    href: '/wms/trails/proof',
    count: stats?.events?.PROOF || stats?.totals?.pods || 0
  }];


  return (
    <div className="p-6 space-y-6" data-testid="trail-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("trailVisualization", "Trail Visualization")}</h1>
          <p className="text-muted-foreground">{t("viewAndQuery", "View and query the four parallel trails: Audit, Operational, Scan, Proof")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
            <TabsList>
              <TabsTrigger value="24">{t("24h", "24h")}</TabsTrigger>
              <TabsTrigger value="72">{t("3d", "3d")}</TabsTrigger>
              <TabsTrigger value="168">{t("7d", "7d")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totals?.events?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">{t("totalEvents", "Total Events")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.scans?.OK?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">{t("successfulScans", "Successful Scans")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.scans?.REJECTED?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">{t("rejectedScans", "Rejected Scans")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <FileCheck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pods?.DELIVERED?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">{t("podsCaptured", "PODs Captured")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trail Type Cards */}
      <div className="grid grid-cols-2 gap-4">
        {trailTypes.map((trail) => {
          const Icon = trail.icon;
          return (
            <Card
              key={trail.id}
              className="cursor-pointer hover:shadow-md transition-all group"
              onClick={() => navigate(trail.href)}
              data-testid={`trail-${trail.id}`}>
              
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${trail.color} text-white`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg">{trail.name}</h3>
                      <Badge variant="secondary">{trail.count.toLocaleString()}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{trail.description}</p>
                    <div className="flex items-center gap-1 mt-3 text-sm text-primary group-hover:underline">{t("viewTrail", "View Trail")}

                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}
      </div>

      {/* Quick Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />{t("quickEntitySearch", "Quick Entity Search")}

          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("orderId", "Order ID")}</label>
              <input
                type="text"
                placeholder="ORD-001"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="search-order-id" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("shipmentId", "Shipment ID")}</label>
              <input
                type="text"
                placeholder="SHP-001"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="search-shipment-id" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("routeId", "Route ID")}</label>
              <input
                type="text"
                placeholder="RT-001"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="search-route-id" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("packageId", "Package ID")}</label>
              <input
                type="text"
                placeholder="PKG-001"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="search-package-id" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">&nbsp;</label>
              <Button className="w-full">
                <Search className="h-4 w-4 mr-2" />{t("search", "Search")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* POD Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />{t("podStatusDistribution", "POD Status Distribution")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500" />{t("delivered", "Delivered")}

                  </span>
                  <span className="font-medium">{stats?.pods?.DELIVERED || 0}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${(stats?.pods?.DELIVERED || 0) / (stats?.totals?.pods || 1) * 100}%` }} />
                  
                </div>
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500" />{t("failed", "Failed")}

                  </span>
                  <span className="font-medium">{stats?.pods?.FAILED || 0}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all"
                    style={{ width: `${(stats?.pods?.FAILED || 0) / (stats?.totals?.pods || 1) * 100}%` }} />
                  
                </div>
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500" />{t("partial", "Partial")}

                  </span>
                  <span className="font-medium">{stats?.pods?.PARTIAL || 0}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all"
                    style={{ width: `${(stats?.pods?.PARTIAL || 0) / (stats?.totals?.pods || 1) * 100}%` }} />
                  
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />{t("scanIssues", "Scan Issues")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("rejected", "Rejected")}</span>
                <Badge variant="destructive">{stats?.scans?.REJECTED || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("warnings", "Warnings")}</span>
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  {stats?.scans?.WARNING || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("successRate", "Success Rate")}</span>
                <Badge variant="default">
                  {stats?.totals?.scans ?
                  Math.round((stats?.scans?.OK || 0) / stats.totals.scans * 100) : 0}%
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default TrailDashboard;